import {
  roleLabel,
  useGetAttributeProperties,
  useGetRoleEligibleEntities,
} from '@commenda/api';
import {
  Badge,
  Button,
  Checkbox,
  FullscreenModal,
  FullscreenModalContent,
  Icons,
  Loading,
  LoadingIndicator,
} from '@commenda/kakashi';
import { useMemo, useRef, useState } from 'react';
import { KeyPersonForm } from './KeyPersonForm';
import {
  AssignRoleStep,
  type AssignRoleStepHandle,
  EntityFlag,
} from './AssignRoleStep';
import {
  IncorporationFormHeader,
  StepsSidebar,
} from '../../form-builder/components/new-incorp-layout';

type WizardStep = 'select' | 'roles';

export const AssignRolesWizard = ({
  isOpen,
  onClose,
  keyPersonId,
  keyPersonName,
  companyId,
  defaultSelectedCorporationId,
  createPerson = false,
}: {
  isOpen: boolean;
  onClose: () => void;
  keyPersonId: number;
  keyPersonName: string;
  companyId: number;
  /** Pre-select this entity and open straight at role config (entity-scoped add-role flow). */
  defaultSelectedCorporationId?: number;
  /** Prepend an "Add key person" step that creates the person, then assigns roles to them. */
  createPerson?: boolean;
}) => {
  // In createPerson mode there's no person until the first step creates one; role-side keys off this.
  const [createdPerson, setCreatedPerson] = useState<{
    id: number;
    name: string;
  } | null>(null);
  const [personFormState, setPersonFormState] = useState({
    isSaving: false,
    canSubmit: false,
  });
  const effectiveKeyPersonId = createPerson
    ? createdPerson?.id ?? 0
    : keyPersonId;
  const displayName = createPerson
    ? createdPerson?.name ?? 'this person'
    : keyPersonName;
  const { data: entities, isLoading } = useGetRoleEligibleEntities(
    effectiveKeyPersonId,
    { enabled: isOpen && effectiveKeyPersonId > 0 }
  );
  // Held-role badges label their buckets from the backend registry.
  const { data: registry } = useGetAttributeProperties(companyId);

  const initialStep: WizardStep =
    !createPerson && defaultSelectedCorporationId ? 'roles' : 'select';
  const [step, setStep] = useState<WizardStep>(initialStep);
  const [entitiesOpen, setEntitiesOpen] = useState(true);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(() =>
    defaultSelectedCorporationId
      ? new Set([defaultSelectedCorporationId])
      : new Set()
  );

  // The roles step delegates to AssignRoleStep; the footer drives its submit via ref.
  const roleStepRef = useRef<AssignRoleStepHandle>(null);
  const [roleStepState, setRoleStepState] = useState({
    canSubmit: false,
    isSubmitting: false,
  });

  const rows = useMemo(() => entities ?? [], [entities]);
  const selectedEntities = useMemo(
    () => rows.filter((e) => selectedIds.has(e.corporationId)),
    [rows, selectedIds]
  );

  const toggleEntity = (corporationId: number) =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(corporationId)) {
        next.delete(corporationId);
      } else next.add(corporationId);
      return next;
    });

  const allSelected = rows.length > 0 && selectedIds.size === rows.length;
  const someSelected = selectedIds.size > 0 && !allSelected;
  const toggleAll = () => {
    if (allSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(rows.map((e) => e.corporationId)));
    }
  };

  const handleClose = () => {
    setStep(initialStep);
    setSelectedIds(
      defaultSelectedCorporationId
        ? new Set([defaultSelectedCorporationId])
        : new Set()
    );
    setCreatedPerson(null);
    setPersonFormState({ isSaving: false, canSubmit: false });
    onClose();
  };

  return (
    <FullscreenModal isOpen={isOpen} onClose={handleClose} enableEscapeKey>
      <IncorporationFormHeader onClose={handleClose} />
      <FullscreenModalContent className="flex min-h-0 items-start justify-center overflow-y-auto bg-bg-light">
        <div className="flex w-full max-w-[1190px] items-start gap-7xl py-5xl pb-8xl">
          <StepsSidebar
            className="top-[80px] pt-9xl"
            steps={
              createPerson
                ? [
                    {
                      id: 'person',
                      text: 'Add key person',
                      isCompleted: step === 'roles',
                    },
                    { id: 'roles', text: 'Assign role', isCompleted: false },
                  ]
                : [
                    {
                      id: 'entities',
                      text: 'Select entities',
                      isCompleted: step === 'roles',
                    },
                    {
                      id: 'roles',
                      text: 'Add role information',
                      isCompleted: false,
                    },
                  ]
            }
            activeStepIndex={step === 'select' ? 0 : 1}
            // No sidebar nav in create mode — the person is already created, so there's no going back.
            onStepClick={(index) => {
              if (!createPerson) setStep(index === 0 ? 'select' : 'roles');
            }}
            supportCard={{}}
          />

          <div className="flex min-w-0 flex-1 flex-col gap-5xl">
            <div className="flex flex-col gap-xs">
              <h1 className="display-xs-bold text-text-primary">
                {createPerson && step === 'select'
                  ? 'Add key person'
                  : `Add role(s) for ${displayName}`}
              </h1>
              <p className="txt-small-regular text-text-secondary">
                {createPerson && step === 'select'
                  ? 'Individuals who play a vital role in your company.'
                  : "Define this individual's relationship to the entity."}
              </p>
            </div>

            {step === 'select' ? (
              createPerson ? (
                isOpen && (
                  <KeyPersonForm
                    companyId={companyId}
                    formId="add-key-person-role-form"
                    onSaved={(id, name) => {
                      setCreatedPerson({ id, name });
                      setStep('roles');
                    }}
                    onStateChange={setPersonFormState}
                  />
                )
              ) : (
                <div className="flex flex-col overflow-hidden rounded-md border border-border-primary bg-bg-primary">
                <button
                  type="button"
                  onClick={() => setEntitiesOpen((o) => !o)}
                  className="flex items-center justify-between border-b border-border-primary p-3xl text-left"
                >
                  <span className="txt-large-bold text-text-primary">
                    Entities
                  </span>
                  <Icons.ChevronDown
                    className={`size-4 shrink-0 text-text-quaternary transition-transform ${
                      entitiesOpen ? 'rotate-180' : ''
                    }`}
                  />
                </button>
                {entitiesOpen &&
                  (isLoading ? (
                    <div className="flex items-center justify-center py-4xl">
                      <Loading />
                    </div>
                  ) : rows.length === 0 ? (
                    <p className="p-3xl txt-small-regular text-text-tertiary">
                      No entities are available for new role assignments.
                    </p>
                  ) : (
                    <div className="p-3xl">
                      <div className="flex flex-col">
                        <div className="flex h-10 items-center gap-lg border-b border-border-secondary px-xl">
                          <Checkbox
                            checked={someSelected ? 'indeterminate' : allSelected}
                            onCheckedChange={toggleAll}
                            aria-label="Select all entities"
                          />
                          <p className="txt-extra-small-bold flex-1 text-text-primary">
                            Entity
                          </p>
                          <p className="txt-extra-small-bold flex-1 text-text-primary">
                            Active roles
                          </p>
                        </div>
                        {rows.map((entity) => (
                          <label
                            key={entity.corporationId}
                            className="flex min-h-11 cursor-pointer items-center gap-lg border-b border-border-secondary px-xl"
                          >
                            <Checkbox
                              checked={selectedIds.has(entity.corporationId)}
                              onCheckedChange={() =>
                                toggleEntity(entity.corporationId)
                              }
                              aria-label={`Select ${entity.corporationName ?? 'entity'}`}
                            />
                            <div className="flex min-w-0 flex-1 items-center gap-md">
                              {entity.country && (
                                <EntityFlag
                                  country={entity.country}
                                  name={entity.corporationName ?? undefined}
                                />
                              )}
                              <span className="truncate txt-small-regular text-text-primary">
                                {entity.corporationName ??
                                  `Entity #${entity.corporationId}`}
                              </span>
                            </div>
                            <div className="flex flex-1 flex-wrap items-center gap-xs">
                              {entity.heldRoleKeys.length === 0 ? (
                                <span className="txt-small-regular text-text-primary">
                                  -
                                </span>
                              ) : (
                                entity.heldRoleKeys.map((k) => (
                                  <Badge key={k} variant="gray" size="sm">
                                    {roleLabel(registry, k)}
                                  </Badge>
                                ))
                              )}
                            </div>
                          </label>
                        ))}
                      </div>
                    </div>
                  ))}
              </div>
              )
            ) : createPerson && isLoading && selectedEntities.length === 0 ? (
              <div className="flex items-center justify-center py-4xl">
                <Loading />
              </div>
            ) : (
              <AssignRoleStep
                ref={roleStepRef}
                keyPersonId={effectiveKeyPersonId}
                companyId={companyId}
                entities={selectedEntities}
                onAssigned={handleClose}
                onStateChange={setRoleStepState}
              />
            )}

            <div className="flex items-center justify-end gap-xl">
              {step === 'select' ? (
                <>
                  <Button variant="secondary" onClick={handleClose}>
                    Cancel
                  </Button>
                  {createPerson ? (
                    <Button
                      type="submit"
                      form="add-key-person-role-form"
                      disabled={
                        !personFormState.canSubmit || personFormState.isSaving
                      }
                    >
                      {personFormState.isSaving ? (
                        <LoadingIndicator size={16} />
                      ) : null}
                      <span>Next</span>
                    </Button>
                  ) : (
                    <Button
                      onClick={() => setStep('roles')}
                      disabled={selectedIds.size === 0}
                    >
                      Next
                    </Button>
                  )}
                </>
              ) : (
                <>
                  {/* Create mode has no Back — the person is already created. */}
                  <Button
                    variant="secondary"
                    onClick={createPerson ? handleClose : () => setStep('select')}
                  >
                    {createPerson ? 'Cancel' : 'Back'}
                  </Button>
                  <Button
                    onClick={() => roleStepRef.current?.submit()}
                    disabled={!roleStepState.canSubmit}
                  >
                    {createPerson ? 'Add key person role' : 'Assign role(s)'}
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      </FullscreenModalContent>
    </FullscreenModal>
  );
};
